import withRouter from "@common/withRouter";
import QuizQuestionnaire, { ISelectedOption } from "@components/QuizQuestionaire";
import { errorToast } from "@components/Toaster";
import { IRouterProps } from "@Interface/Login";
import { selectors } from "@redux/index";
import { useTypedSelector } from "@redux/store";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageHeader from "@components/PageHeader";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import SuccessPopup from "@components/PopupModals/SuccessPopup";
import FailurePopup from "@components/PopupModals/FailurePopup";
import { t } from "@utils/translations";
import Constants from "@utils/constants";
import { shallowEqual } from "react-redux";
import useApiQuery from "@hooks/useApiQuery";
import useApiMutation from "@hooks/useApiMutation";
import storeErrorLog from "@utils/storeErrorLogs";
import Button from "@components/Button";
import EmptyIcon from "@assets/images/no-data.png";
import { pushToDataLayer } from "@utils/gtm";
import { DataLayerExternalKeys } from "@common/commonDatalayerKeys";

export interface QuizOption {
  quizOptionId: number;
  twOption: string;
  enOption: string;
  isCorrect: number;
  enQuestion: string;
  enAnswer: string;
}

export interface QuizQuestion {
  twQuizQuestionId: number;
  twQuestion: string;
  enQuestion: string;
  options: QuizOption[];
}

const Quiz: React.FC<IRouterProps> = ({ router }) => {
  const [selectedOption, setSelectedOption] = useState<ISelectedOption | null>(null);
  const [successPopup, setSuccessPopup] = useState<boolean>(false)
  const [failedPopup, setFailedPopup] = useState<boolean>(false)
  const quizStartedTracked = useRef(false);

  const missionMasterId = router?.location?.state?.missionMasterId as number;
  const campaignMasterId = Number(import.meta.env.VITE_CAMPAIGN_MASTER_ID);
  const dataLayerExternalKeys = DataLayerExternalKeys();
  const { loggedInDetails } = useTypedSelector((state) => ({
    loggedInDetails: selectors.loggedInDetails(state)
  }), shallowEqual);

  useEffect(() => {
    if (!missionMasterId) {
      router.navigate("/home");
    } else {
      submitDatalayerEvent('page_view');
    }
  }, [missionMasterId, router]);

  const {
    data: quizData,
    isFetching: isQuizLoading,
    isFetched: isQuizFetched,
  } = useApiQuery({
    endpoint: Constants.GET_QUIZ_QUESTIONS,
    queryKey: ["quizQuestions"],
    params: {
      memberId: loggedInDetails?.memberId,
      missionMasterId: missionMasterId,
      campaignMasterId: campaignMasterId,
    },
  });

  const quizQuestions: QuizQuestion[] = useMemo(() => {
    if (quizData?.success && quizData.data?.length) {
      return quizData.data;
    }
    return [];
  }, [quizData]);

  const noData =
    isQuizFetched &&
    !isQuizLoading &&
    (!quizData?.success || !quizData.data?.length);

  useEffect(() => {
    if (!isQuizFetched || isQuizLoading || !noData) return;
    errorToast(quizData?.message ?? t("quiz.fetchError"));
  }, [isQuizFetched, isQuizLoading, noData, quizData?.message]);

  useEffect(() => {
    if (quizQuestions.length) {
      quizStartedTracked.current = false;
    }
  }, [quizQuestions]);

  const submitDatalayerEvent = (key: string) => {
    switch (key) {
      case 'page_view':
        pushToDataLayer({
          'event': 'eventTracker',
          'custom_event_name': 'pageView',
          'page_name': 'daily challenge started page',
          'page_type': 'challenge page',
          ...dataLayerExternalKeys,
        });
        break;
      case 'quiz_started':
        pushToDataLayer({
          'event': 'eventTracker',
          'custom_event_name': 'daily_challenge_started'
        });
        break;
      case 'quiz_submitted':
        pushToDataLayer({
          'event': 'eventTracker',
          'custom_event_name': 'answer_submit',
          [quizQuestions[0].enQuestion.toLowerCase()]: selectedOption?.enAnswer.toLowerCase() ?? '',
          'place_of_question': 'daily challenge'
        });
        pushToDataLayer({
          'event': 'eventTracker',
          'custom_event_name': 'daily_challenge_submitted'
        });
        break;
      case 'quiz_success_modal':
        pushToDataLayer({
          'event': 'eventTracker',
          'custom_event_name': 'page_view',
          'page_name': 'daily challenge success popup page',
          'page_type': 'overlay page',
          ...dataLayerExternalKeys,
        });
        pushToDataLayer({
          'event': 'eventTracker',
          'custom_event_name': 'daily_challenge_completed'
        });
        break;
      case 'quiz_failed_modal':
        pushToDataLayer({
          'event': 'eventTracker',
          'custom_event_name': 'page_view',
          'page_name': 'daily challenge retry popup page',
          'page_type': 'overlay page',
          ...dataLayerExternalKeys,
        });
        pushToDataLayer({
          'event': 'eventTracker',
          'custom_event_name': 'daily_challenge_wrong_answer',
        });
        break;
      case 'quiz_retry_cta_click':
        pushToDataLayer({
          'event': 'eventTracker',
          'custom_event_name': 'cta_click',
          'cta_name': 'return to task',
          'cta_type': 'button',
          'cta_position': 'body'
        });
        break;
      default:
        break;
    }
  }

  const handleSelectedOption = useCallback((option: ISelectedOption) => {
    setSelectedOption((prev) => {
      if (!quizStartedTracked.current && !prev) {
        quizStartedTracked.current = true;
        submitDatalayerEvent('quiz_started');
      }
      return option;
    });
  }, [submitDatalayerEvent]);

  const submitQuizMutation = useApiMutation<{ success: boolean; message?: string; data?: any }>({
    endpoint: Constants.SUBMIT_QUIZ_QUESTIONS,
    method: "POST",
  });

  const allAnswered =
    selectedOption !== null &&
    quizQuestions.length > 0;

  const checkQuizSubmission = () => {
    if (!allAnswered || !selectedOption) {
      errorToast(t("quiz.answerRequired"));
      return;
    }
    const incorrectAnswers = [selectedOption].filter((item) => item.isCorrect === 0);
    submitQuiz(incorrectAnswers);
  };

  const submitQuiz = async (incorrectAnswers: ISelectedOption[]) => {
    submitDatalayerEvent('quiz_submitted');
    if (incorrectAnswers.length > 0) {
      setFailedPopup(true);
      submitDatalayerEvent('quiz_failed_modal');
    } else {
      const response = await submitQuizMutation.mutateAsync({
        memberId: loggedInDetails?.memberId,
        campaignMasterId: campaignMasterId,
        missionMasterId: missionMasterId,
      });
      if (response.success) {
        submitDatalayerEvent('quiz_success_modal');
        setSuccessPopup(true);
      } else {
        errorToast(response.message ?? "Error in submitting quiz");
        storeErrorLog({ message: response.message, stack: "submit quiz" }, loggedInDetails?.memberId);
      }
    }
  };

  const onCloseFailedModal = () => {
    setFailedPopup(false);
    submitDatalayerEvent('quiz_retry_cta_click');
  }

  return (
    <>
      <div className="flex h-screen flex-col bg-white">
        <PageHeader title="每日挑戰" onBack={() => router.navigate(-1)} />

        <div className="flex flex-1 flex-col overflow-hidden">
          <div
            className={
              noData
                ? "flex flex-1 items-center justify-center px-4"
                : "flex-1 overflow-y-auto pt-4 pb-4"
            }
          >
            {isQuizLoading ? (
              <div className="space-y-4 px-4 w-full">
                {Array.from({ length: 2 }).map((_, index) => (
                  <Skeleton key={index} height={100} borderRadius={12} />
                ))}
              </div>
            ) : noData ? (
              <div className="flex w-full max-w-sm flex-col items-center justify-center rounded-xl bg-white px-4 py-15 text-center">
                <img
                  src={EmptyIcon}
                  alt=""
                  className="mx-auto mb-10 h-14 w-14 opacity-40"
                />
                <p className="text-(--dark-gray) opacity-60">
                  {t("quiz.noDataFound")}
                </p>
              </div>
            ) : (
              <>
                {quizQuestions.map((question, index) => (
                  <QuizQuestionnaire
                    key={question.twQuizQuestionId}
                    questionKey={index}
                    questionId={question.twQuizQuestionId}
                    question={question.twQuestion}
                    options={question.options}
                    handleSelectedOption={handleSelectedOption}
                    selectedOption={selectedOption}
                  />
                ))}
                <div className="px-4 pt-6 pb-6 mt-6">
                  <Button
                    label={t("quiz.confirmAnswer")}
                    classNames="w-full"
                    onClick={checkQuizSubmission}
                    disabled={!allAnswered}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {successPopup && (
        <SuccessPopup
          content="quiz"
          pageTitle="每日挑戰"
          heading={t("quiz.title")}
          subtitle={t("quiz.subtitle")}
          rewardAmount={+3}
          footerText={t("quiz.footerText")}
          primaryLabel={t("quiz.backToRewards")}
          // secondaryLabel={t("feedback.backToRewards")}
        />
      )}
      {failedPopup && quizQuestions.length > 0 && (
        <FailurePopup
          question={quizQuestions[0].twQuestion}
          onClose={onCloseFailedModal}
        />
      )}
    </>
  );
};

export default withRouter(Quiz);
